import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import $ from "jquery";
import "jquery-ui-dist/jquery-ui";
import "jquery-ui-dist/jquery-ui.css";
import { getHotelSuggestions } from "../../Services/HotelAutoSuggestApi";
import countryData from "../../Services/Country.json";

const capitalizeWords = (str) => {
  if (!str) return "";
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const MAX_ROOMS = 6;

const ModifySearch = ({ onSearch }) => {
  const navigate = useNavigate();

  const [city, setCity] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const [rooms, setRooms] = useState([
    { adults: 1, children: 0, childAges: [] },
  ]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Corporate Travel States
  const [travelType, setTravelType] = useState("BusinessTravel");
  const [corporateCode, setCorporateCode] = useState("");
  const [corporateTraveler, setCorporateTraveler] = useState("");
  const [nationality, setNationality] = useState("106");
  const [starRatings, setStarRatings] = useState([]);
  const [showStarDropdown, setShowStarDropdown] = useState(false);

  const savedDataRef = useRef({});

  const totalRooms = rooms.length;
  const totalAdults = rooms.reduce((s, r) => s + r.adults, 0);
  const totalChildren = rooms.reduce((s, r) => s + r.children, 0);

  // ── Load sessionStorage ───────────────────────────────────────────────────
  useEffect(() => {
    const raw = sessionStorage.getItem("hotelSearchData");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      savedDataRef.current = data;
      if (data.destinationName) setCity(capitalizeWords(data.destinationName));
      if (data.selectedLocation) setSelectedLocation(data.selectedLocation);
      if (data.roomGuests && data.roomGuests.length > 0) {
        setRooms(
          data.roomGuests.map((r) => ({
            adults: r.noOfAdults || 1,
            children: r.noOfChilds || 0,
            childAges:
              r.childAge && r.childAge.length > 0
                ? r.childAge
                : Array(r.noOfChilds || 0).fill(1),
          })),
        );
      }
      if (data.travelType) setTravelType(data.travelType);
      if (data.corporateCode) setCorporateCode(data.corporateCode);
      if (data.corporateTraveler)
        setCorporateTraveler(
          Array.isArray(data.corporateTraveler)
            ? data.corporateTraveler[0]
            : data.corporateTraveler || "",
        );
      if (data.nationality) setNationality(data.nationality);
      if (data.starRatings) {
        setStarRatings(data.starRatings);
      } else if (data.starRating) {
        setStarRatings([data.starRating]);
      }
    } catch (e) {
      console.error("ModifySearch:", e);
    }
  }, []);

  // ── Datepickers ───────────────────────────────────────────────────────────
  useEffect(() => {
    let initial = new Date();
    try {
      const d = JSON.parse(sessionStorage.getItem("hotelSearchData") || "{}");
      if (d.checkInDate) {
        const [y, m, day] = d.checkInDate.split("-").map(Number);
        initial = new Date(y, m - 1, day);
      }
    } catch (_) {}
    $("#ms_checkIn").datepicker({
      dateFormat: "dd-M-yy",
      minDate: 0,
      maxDate: "+4m",
      showAnim: "fadeIn",
      onSelect: function (dateText) {
        const date = $(this).datepicker("getDate");
        $("#ms_checkOut").datepicker("option", "minDate", date);
        const checkOutDate = $("#ms_checkOut").datepicker("getDate");
        if (!checkOutDate || checkOutDate <= date) {
          const nextDay = new Date(date);
          nextDay.setDate(date.getDate() + 1);
          $("#ms_checkOut").datepicker("setDate", nextDay);
        }
      },
    });
    $("#ms_checkIn").datepicker("setDate", initial);
  }, []);

  useEffect(() => {
    let initial = new Date();
    initial.setDate(initial.getDate() + 1);
    try {
      const d = JSON.parse(sessionStorage.getItem("hotelSearchData") || "{}");
      if (d.checkOutDate) {
        const [y, m, day] = d.checkOutDate.split("-").map(Number);
        initial = new Date(y, m - 1, day);
      }
    } catch (_) {}
    const checkInDate = $("#ms_checkIn").datepicker("getDate") || new Date();
    $("#ms_checkOut").datepicker({
      dateFormat: "dd-M-yy",
      minDate: checkInDate,
      maxDate: "+4m",
      showAnim: "fadeIn",
    });
    $("#ms_checkOut").datepicker("setDate", initial);
  }, []);

  // ── Close all on outside click ────────────────────────────────────────────
  useEffect(() => {
    const close = () => {
      setShowSuggestions(false);
      setShowDropdown(false);
      setShowStarDropdown(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── City ──────────────────────────────────────────────────────────────────
  const handleCityChange = (e) => {
    setCity(e.target.value);
    setIsUserInteracting(true);
  };

  useEffect(() => {
    // Only fetch suggestions if the user is actively typing or interacting
    if (city.length > 2 && isUserInteracting) {
      const handler = setTimeout(async () => {
        const results = await getHotelSuggestions(city);
        setFilteredSuggestions(results);
        setShowSuggestions(results.length > 0);
      }, 500); // 500ms debounce

      return () => clearTimeout(handler);
    } else {
      // If not interacting or length < 3, don't show suggestions
      // But don't clear filteredSuggestions if we just selected one
      if (!isUserInteracting) {
        setShowSuggestions(false);
      }
    }
  }, [city, isUserInteracting]);
  const handleSuggestionSelect = (item) => {
    setIsUserInteracting(false); // Stop interaction before updating city to prevent useEffect trigger
    setCity(item.name);
    setSelectedLocation(item);
    setShowSuggestions(false);
  };

  // ── Toggle shared dropdown ────────────────────────────────────────────────
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown((prev) => !prev);
  };

  // ── Room helpers ──────────────────────────────────────────────────────────
  const addRoom = () => {
    if (rooms.length < MAX_ROOMS)
      setRooms([...rooms, { adults: 1, children: 0, childAges: [] }]);
  };
  const removeRoom = () => {
    if (rooms.length > 1) setRooms(rooms.slice(0, -1));
  };
  const updateRoom = (index, field, value) => {
    const updated = [...rooms];
    if (field === "children") {
      const n = Number(value);
      const ages = updated[index].childAges || [];
      updated[index].childAges =
        n > ages.length
          ? [...ages, ...Array(n - ages.length).fill(1)]
          : ages.slice(0, n);
      updated[index].children = n;
    } else if (field === "childAge") {
      updated[index].childAges[value.childIndex] = Number(value.age);
    } else {
      updated[index][field] = Number(value);
    }
    setRooms(updated);
  };

  // ── Modify ────────────────────────────────────────────────────────────────
  const handleModify = async (e) => {
    e.preventDefault();
    if (!city.trim()) {
      alert("Please enter a destination");
      return;
    }

    const checkInDate = $("#ms_checkIn").datepicker("getDate");
    const checkOutDate = $("#ms_checkOut").datepicker("getDate");

    if (checkInDate && checkOutDate && checkOutDate <= checkInDate) {
      alert("Check-out date must be at least one day after the Check-in date");
      return;
    }

    let finalHotelIds = [];
    let finalDestinationName = capitalizeWords(city);

    if (selectedLocation && selectedLocation.name.toLowerCase() === city.toLowerCase()) {
      finalHotelIds =
        selectedLocation.hotelIds ||
        (selectedLocation.type === "hotel" ? [selectedLocation.id] : []);
      finalDestinationName = selectedLocation.name;
      setCity(selectedLocation.name);
    } else if (
      city.toLowerCase() ===
      (savedDataRef.current.destinationName || "").toLowerCase()
    ) {
      finalHotelIds = savedDataRef.current.hotelIds || [];
      finalDestinationName = capitalizeWords(savedDataRef.current.destinationName);
    } else {
      try {
        const results = await getHotelSuggestions(city);
        if (results && results.length > 0) {
          const match = results[0];
          finalHotelIds =
            match.hotelIds || (match.type === "hotel" ? [match.id] : []);
          finalDestinationName = match.name;
          setCity(match.name);
        } else {
          alert(
            "Could not find any matching city or hotel. Please select from the dropdown."
          );
          return;
        }
      } catch (e) {
        alert("Error fetching location data. Please try again.");
        return;
      }
    }

    const fmt = (date) => {
      if (!date) return "";
      return `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}`;
    };

    const roomGuests = rooms.map((r) => ({
      noOfAdults: Number(r.adults),
      noOfChilds: Number(r.children),
      childAge: r.childAges || [],
    }));
    const searchData = {
      ...savedDataRef.current,
      checkInDate: fmt(checkInDate),
      checkOutDate: fmt(checkOutDate),
      hotelIds: finalHotelIds,
      roomGuests,
      traceId: "",
      destinationName: finalDestinationName,
      travelType,
      corporateCode,
      corporateTraveler,
      nationality,
      starRatings: starRatings,
      selectedLocation: selectedLocation,
    };
    delete searchData.starRating;
    sessionStorage.setItem("hotelSearchData", JSON.stringify(searchData));
    
    // If a specific hotel was selected, we might want to clear previous hotel/room selection to force re-selection
    if (selectedLocation?.type === 'hotel') {
       sessionStorage.removeItem("selectedHotel");
       sessionStorage.removeItem("selectedRoom");
    }

    if (onSearch) {
      onSearch();
    } else {
      // If it's a specific hotel, we could potentially navigate to details directly
      // but usually we go to results filtered by that ID.
      navigate("/hotel/result");
      window.location.reload();
    }
  };

  return (
    <>
      <section className="modify_serch">
        {/* Travel Mode Radio Buttons */}
        <div className="d-flex flex-wrap gap-4 mb-3 px-3">
          <div className="travel_mode m-0 me-3">
            <div className="form-check form-check-custom form-check-solid d-flex align-items-center">
              <input
                className="form-check-input"
                type="radio"
                name="ms_travelType"
                value="BusinessTravel"
                id="ms_BusinessTravel"
                checked={travelType === "BusinessTravel"}
                onChange={(e) => setTravelType(e.target.value)}
              />
              <label className="form-check-label ms-1 cursor-pointer" htmlFor="ms_BusinessTravel" style={{ color: "#110942", fontWeight: "600" }}>
                Business Travel
              </label>
            </div>
          </div>

          <div className="travel_mode m-0 me-3">
            <div className="form-check form-check-custom form-check-solid d-flex align-items-center">
              <input
                className="form-check-input"
                type="radio"
                name="ms_travelType"
                value="PersonalTravel"
                id="ms_PersonalTravel"
                checked={travelType === "PersonalTravel"}
                onChange={(e) => setTravelType(e.target.value)}
              />
              <label className="form-check-label ms-1 cursor-pointer" htmlFor="ms_PersonalTravel" style={{ color: "#110942", fontWeight: "600" }}>
                Personal Travel
              </label>
            </div>
          </div>

          <div className="travel_mode m-0 me-3">
            <div className="form-check form-check-custom form-check-solid d-flex align-items-center">
              <input
                className="form-check-input"
                type="radio"
                name="ms_travelType"
                value="GuestTravel"
                id="ms_GuestTravel"
                checked={travelType === "GuestTravel"}
                onChange={(e) => setTravelType(e.target.value)}
              />
              <label className="form-check-label ms-1 cursor-pointer" htmlFor="ms_GuestTravel" style={{ color: "#110942", fontWeight: "600" }}>
                Guest Travel
              </label>
            </div>
          </div>
        </div>

        <div className="modify_serch_main pos-relative d-flex">
          {/* City */}
          <div className="col-msd-1 city_hotl pos-relative">
            <div className="form_gp">
              <label>Enter City, Location, Hotel Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter.."
                value={city}
                onChange={handleCityChange}
                style={{ textTransform: "capitalize" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (city.length > 2) {
                    setIsUserInteracting(true);
                    setShowSuggestions(true);
                  }
                }}
                autoComplete="off"
              />
              {showSuggestions && filteredSuggestions.length > 0 && (() => {
                const topSuggestions = filteredSuggestions.filter((item) => item.type === "city");
                const remainingSuggestions = filteredSuggestions.filter((item) => item.type !== "city");
                return (
                  <ul className="autosuggetsList">
                    {/* Cities (Top) */}
                    {topSuggestions.length > 0 && (
                      <>
                        <li
                          className="suggestion-group-header"
                          style={{
                            padding: "8px 12px",
                            background: "#f4f4f4",
                            color: "#110942",
                            fontWeight: "700",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            pointerEvents: "none",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          Cities / Regions
                        </li>
                        {topSuggestions.map((item, index) => {
                          const regionParts = item.region ? item.region.split(",") : [item.name];
                          const topText = regionParts[0].trim();
                          const remainingText = regionParts.slice(1).join(",").trim();
                          return (
                            <li
                              key={`city-${index}`}
                              onClick={() => handleSuggestionSelect(item)}
                              style={{ display: "flex", flexDirection: "row", alignItems: "center", cursor: "pointer" }}
                            >
                              <div className="d-flex align-items-center w-100">
                                <i
                                  className="fa fa-map-marker-alt suggestion-icon"
                                  style={{
                                    fontSize: "16px",
                                    color: "#110942",
                                    marginRight: "12px",
                                    width: "20px",
                                    textAlign: "center",
                                  }}
                                ></i>
                                <div style={{ flex: 1 }}>
                                  <div className="font-600" style={{ fontSize: "14px", color: "#110942", fontWeight: "600", textTransform: "capitalize" }}>
                                    {topText}
                                  </div>
                                  {remainingText && (
                                    <div className="fs-11 text-gray-600" style={{ fontSize: "11px", color: "#7E7979", textTransform: "capitalize" }}>
                                      {remainingText}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </>
                    )}

                    {/* Hotels & Remaining */}
                    {remainingSuggestions.length > 0 && (
                      <>
                        <li
                          className="suggestion-group-header"
                          style={{
                            padding: "8px 12px",
                            background: "#f4f4f4",
                            color: "#110942",
                            fontWeight: "700",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            pointerEvents: "none",
                            borderBottom: "1px solid #e2e8f0",
                            borderTop: topSuggestions.length > 0 ? "1px solid #e2e8f0" : "none",
                          }}
                        >
                          Hotels
                        </li>
                        {remainingSuggestions.map((item, index) => (
                          <li
                            key={`remaining-${index}`}
                            onClick={() => handleSuggestionSelect(item)}
                            style={{ display: "flex", flexDirection: "row", alignItems: "center", cursor: "pointer" }}
                          >
                            <div className="d-flex align-items-center w-100">
                              <i
                                className="fa fa-hotel suggestion-icon"
                                style={{
                                  fontSize: "16px",
                                  color: "#dacd65",
                                  marginRight: "12px",
                                  width: "20px",
                                  textAlign: "center",
                                }}
                              ></i>
                              <div style={{ flex: 1 }}>
                                <div className="font-600" style={{ fontSize: "14px", color: "#110942", fontWeight: "600", textTransform: "capitalize" }}>
                                  {item.name}
                                </div>
                                {item.region && (
                                  <div className="fs-11 text-gray-600" style={{ fontSize: "11px", color: "#7E7979", textTransform: "capitalize" }}>
                                    ({item.region})
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </>
                    )}
                  </ul>
                );
              })()}
            </div>
          </div>

          {/* Check-In */}
          <div className="col-msd-1">
            <div className="form_gp">
              <label>Check-In Date</label>
              <input
                type="text"
                id="ms_checkIn"
                className="form-control"
                placeholder="Date.."
                readOnly
              />
            </div>
          </div>

          {/* Check-Out */}
          <div className="col-msd-1">
            <div className="form_gp">
              <label>Check-Out Date</label>
              <input
                type="text"
                id="ms_checkOut"
                className="form-control"
                placeholder="Date.."
                readOnly
              />
            </div>
          </div>

          {/* Rooms — click opens shared dropdown */}
          <div
            className="col-msd-1"
            onClick={toggleDropdown}
            style={{ cursor: "pointer" }}
          >
            <div className="form_gp">
              <label>Rooms</label>
              <select
                className="form-selct pointer-events-none"
                value={totalRooms}
                onChange={() => {}}
              >
                <option value={totalRooms}>
                  {String(totalRooms).padStart(2, "0")}
                </option>
              </select>
            </div>
          </div>

          {/* Adults — click opens shared dropdown */}
          <div
            className="col-msd-1 cursor-pointer"
            onClick={toggleDropdown}
          >
            <div className="form_gp">
              <label>ADULTS (17+ YRS)</label>
              <select
                className="form-selct pointer-events-none"
                value={totalAdults}
                onChange={() => {}}
              >
                <option value={totalAdults}>
                  {String(totalAdults).padStart(2, "0")}
                </option>
              </select>
            </div>
          </div>

          {/* Children — click opens shared dropdown */}
          <div
            className="col-msd-1 cursor-pointer"
            onClick={toggleDropdown}
          >
            <div className="form_gp">
              <label>CHILD (0-17 YRS)</label>
              <select
                className="form-selct pointer-events-none"
                value={totalChildren}
                onChange={() => {}}
              >
                <option value={totalChildren}>
                  {String(totalChildren).padStart(2, "0")}
                </option>
              </select>
            </div>
          </div>

          {/* Nationality */}
          <div className="col-msd-1">
            <div className="form_gp">
              <label>Nationality</label>
              <select
                className="form-selct"
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
              >
                {countryData.nationalityInfos.map((c) => (
                  <option key={c.countryId} value={c.countryId}>
                    {c.countryName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Star Category */}
          <div
            className="col-msd-1 pos-relative"
            onClick={(e) => {
              e.stopPropagation();
              setShowStarDropdown(!showStarDropdown);
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="form_gp">
              <label>Star </label>
              <div className="form-selct d-flex justify-content-between align-items-center" style={{ height: "44px", background: "#fff", cursor: "pointer" }}>
                <span style={{ fontSize: "14px", fontWeight: "400" }}>
                  {starRatings.length === 0
                    ? "All Stars"
                    : starRatings.length === 5
                    ? "All Stars"
                    : `${starRatings.sort().join(", ")} Star`}
                </span>
                <i className="fa fa-chevron-down text-muted" style={{ fontSize: "10px" }}></i>
              </div>
            </div>

            {showStarDropdown && (
              <div
                className="star-rating-dropdown"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  width: "180px",
                  background: "#fff",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                  borderRadius: "6px",
                  padding: "12px",
                  zIndex: 1000,
                  marginTop: "5px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {[1, 2, 3, 4, 5].map((star) => {
                  const val = star.toString();
                  const isChecked = starRatings.includes(val);
                  return (
                    <div className="form-check mb-2" key={star}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`modify-star-${star}`}
                        checked={isChecked}
                        onChange={() => {
                          setStarRatings((prev) =>
                            prev.includes(val)
                              ? prev.filter((s) => s !== val)
                              : [...prev, val]
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="form-check-label ms-2 cursor-pointer"
                        htmlFor={`modify-star-${star}`}
                        style={{ color: "#110942", fontWeight: "600" }}
                      >
                        {star} Star
                      </label>
                    </div>
                  );
                })}
                <div className="border-top pt-2 mt-2 text-end">
                  <button
                    className="btn btn-sm btn-primary done_selcts"
                    onClick={() => setShowStarDropdown(false)}
                    style={{ fontSize: "11px", padding: "3px 10px" }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modify Button */}
          <div className="col-msd-1">
            <div className="form_gp">
              <label>&nbsp;</label>
              <a
                className="Serch_msd cursor-pointer"
                onClick={handleModify}
              >
                Modify
              </a>
            </div>
          </div>

          {showDropdown && (
            <div
              className="hotel hotel-dropdown-container d-block"
              style={{
                position: "absolute",
                top: "100%",
                right: "0",
                width: "100%",
                maxWidth: "350px",
                zIndex: 1000,
                marginTop: "5px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                background: "#fff"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="traveller-calulate-persons">
                <div className="max-h-350 overflow-y-auto pr-1">
                  {rooms.map((room, index) => (
                    <div className="passengers" key={index}>
                      <h6>Room {index + 1}</h6>

                      {/* Adults */}
                      <div className="passengers-type">
                        <div className="text">
                          <div className="type-label">
                            <p>Adult</p>
                            <span>17+ yrs</span>
                          </div>
                        </div>
                        <div className="button-set">
                          <select
                            value={room.adults}
                            onChange={(e) =>
                              updateRoom(index, "adults", e.target.value)
                            }
                            className="form-select form-select-solid"
                          >
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="passengers-type">
                        <div className="text">
                          <div className="type-label">
                            <p>Children</p>
                            <span>0 – 17 yrs</span>
                          </div>
                        </div>
                        <div className="button-set">
                          <select
                            value={room.children}
                            onChange={(e) =>
                              updateRoom(index, "children", e.target.value)
                            }
                            className="form-select form-select-solid"
                          >
                            {[0, 1, 2, 3, 4].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Child Ages */}
                      {room.children > 0 && (
                        <div className="child-age-box">
                          <p className="child-age-label">
                            Child Ages
                          </p>
                          <div className="d-flex flex-wrap">
                            {room.childAges.map((age, childIdx) => (
                              <div key={childIdx} className="child-slot">
                                <span className="fs-10 d-block">
                                  Child {childIdx + 1}
                                </span>
                                <select
                                  value={age}
                                  onChange={(e) =>
                                    updateRoom(index, "childAge", {
                                      childIndex: childIdx,
                                      age: e.target.value,
                                    })
                                  }
                                  className="form-select form-select-sm fs-12"
                                  style={{ width: "70px" }}
                                >
                                  {[...Array(17).keys()].map((n) => (
                                    <option key={n + 1} value={n + 1}>
                                      {n + 1} Year
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="col-12 mt-3 pt-2 border-top-eee d-flex flex-wrap">
                  <button
                    className="btn btn-primary btn-sm m-1"
                    onClick={addRoom}
                    disabled={rooms.length === MAX_ROOMS}
                  >
                    Add Room
                  </button>
                  <button
                    className="btn btn-primary btn-sm m-1"
                    onClick={removeRoom}
                    disabled={rooms.length === 1}
                  >
                    Remove Room
                  </button>
                  <button
                    className="btn btn-danger btn-sm done_selcts m-1"
                    onClick={() => setShowDropdown(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ── end ONE dropdown ── */}
        </div>
      </section>
    </>
  );
};

export default ModifySearch;
